/* global BaseUrl */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { helperGetRelativeUrl } from 'helpers/RouteHelper';
import { format, secondsToTimeString, FORMAT_YEAR_MONTH_DAY } from 'helpers/DateTimeHelper';

const CONTEXT = 'https://schema.org/';
const CONTACT_POINT = 'ContactPoint';
const DEFAULT_COUNTRY = 'US';
const SEARCH_ACTION = 'SearchAction';
const TYPE_BREADCRUMB = 'BreadcrumbList';
const TYPE_BROADCAST_EVENT = 'BroadcastEvent';
const TYPE_FAQ = 'FAQPage';
const TYPE_LIST_ITEM = 'ListItem';
const TYPE_MOVIE = 'Movie';
const TYPE_ORGANIZATION = 'Organization';
const TYPE_PERSON = 'Person';
const TYPE_TV_EPISODE = 'TVEpisode';
const TYPE_TV_SERIES = 'TVSeries';
const TYPE_VIDEO_OBJECT = 'VideoObject';
const TYPE_WEBSITE = 'WebSite';

export const SCHEMA_TYPES = {
  BREADCRUMBS: 'BREADCRUMBS',
  FAQ: 'FAQ',
  MOVIES: 'MOVIES',
  ORGANIZATION: 'ORGANIZATION',
  PERSON: 'PERSON',
  SERIES: 'SERIES',
  VIDEO_OBJECT: 'VIDEO_OBJECT',
  WATCH_CLIP: 'WATCH_CLIP',
  WATCH_EPISODE: 'WATCH_EPISODE',
  WEBSITE: 'WEBSITE',
};

const createWatchEpisodeSchema = ({ item }) => {
  if (!item) return undefined;
  const {
    datePublished,
    description,
    detailData,
    genres,
    playerScreenUrl,
    seriesName,
    seriesScreenUrl,
  } = item;
  if (!detailData) return undefined;
  const url = `${BaseUrl}${helperGetRelativeUrl({ url: playerScreenUrl || seriesScreenUrl })}`;
  return {
    '@context': CONTEXT,
    '@type': TYPE_TV_EPISODE,
    url,
    name: seriesName,
    description,
    genre: genres?.toString(),
    datePublished: format(new Date(datePublished), 'YYYY-MM-DD'),
    '@logo': detailData?.images?.logo?.raw,
  };
};

const createWatchClipSchema = ({ item }) => {
  if (!item) return undefined;
  const { name, description, datePublished, duration, images } = item;
  return {
    '@context': CONTEXT,
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl: _.get(images, 'still.raw', null),
    uploadDate: format(new Date(datePublished), 'YYYY-MM-DD'),
    duration,
  };
};

const createMoviesSchema = ({ item }) => {
  if (!item) return undefined;
  const { name, description, duration, images, genres, keywords } = item;
  return {
    '@context': CONTEXT,
    '@type': TYPE_MOVIE,
    name,
    image: images?.still?.raw ?? null,
    description,
    genre: genres?.[0],
    keywords: keywords?.join(', '),
    duration,
  };
};

const createSeriesDetailSchema = ({ item }) => {
  if (!item) return null;
  const {
    credits,
    description,
    genres,
    images,
    latestFullEpisodeDate,
    name,
    screenUrl,
    searchKeywords,
  } = item;

  const baseUrl = BaseUrl;
  const url = `${baseUrl}${helperGetRelativeUrl({ url: screenUrl })}`;
  let actor;
  let director;
  const genre = genres?.join(',');
  const datePublished = format(latestFullEpisodeDate, FORMAT_YEAR_MONTH_DAY);
  const keywords = searchKeywords?.join(', ');
  const actors = credits?.member?.filter(item => item?.category?.toLowerCase() === 'cast');
  if (actors.length > 0) {
    actor = actors.map(actor => ({
      '@type': 'Person',
      name: actor.name,
      url: `${url}credits/${actor.alternateId}`,
    }));
  }
  const directorArray = credits?.member?.filter(
    item => item?.category?.toLowerCase() === 'director',
  );
  if (directorArray.length > 0) {
    director = directorArray?.map(d => ({
      '@type': 'Person',
      name: d.name,
      url: `${url}credits/${d.alternateId}`,
    }));
  }
  const image = images?.still?.raw ?? null;
  return {
    '@context': CONTEXT,
    '@type': TYPE_TV_SERIES,
    url,
    name,
    image,
    description,
    genre,
    datePublished,
    keywords,
    ...(actor && { actor }),
    ...(director && { director }),
  };
};

const createPersonSchema = ({ item }) => {
  if (!item) return null;
  const alias = item?.social?.map(item => item?.link) ?? [];
  const baseUrl = BaseUrl;
  const url = `${baseUrl}/personalities/${item?.id}`;
  return {
    '@context': CONTEXT,
    '@type': TYPE_PERSON,
    name: item?.name,
    description: item?.description,
    url,
    image: item?.images?.bioDetail,
    sameAs: alias,
  };
};

const createFAQSchema = ({ item }) => {
  if (!item) return null;
  const faqs = item.map(faq => ({
    '@type': 'Question',
    name: faq?.headline,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq?.body,
    },
  }));

  return {
    '@context': CONTEXT,
    '@type': TYPE_FAQ,
    mainEntity: faqs,
  };
};

const createVideoObjectSchema = ({ item }) => {
  if (!item) return null;

  const videoObject = {
    '@context': CONTEXT,
    '@type': TYPE_VIDEO_OBJECT,
    contentUrl: item?.playerScreenUrl,
    description: item?.description,
    name: item?.name,
    thumbnailUrl: item?.images?.still?.raw || item?.images?.seriesList?.raw,
    uploadDate: format(new Date(item?.datePublished), 'YYYY-MM-DD'),
    duration: secondsToTimeString(item?.durationInSeconds),
    regionsAllowed: 'US',
  };

  const publication = {
    '@type': TYPE_BROADCAST_EVENT,
    name: item?.name,
    isLiveBroadcast: true,
    startDate: item?.datePublished,
    endDate: item?.endDate,
  };

  if (item?.isLiveNow) {
    videoObject.publication = publication;
  } else {
    videoObject.expires = format(new Date(item?.videoRelease?.expires), FORMAT_YEAR_MONTH_DAY);
  }

  return videoObject;
};

const createBreadcrumbSchema = ({ item }) => {
  if (!item) return null;
  const itemListElement = item.map(getBreadcrumbItem);

  return {
    '@context': CONTEXT,
    '@type': TYPE_BREADCRUMB,
    itemListElement,
  };
};

const generateItem = (id, name) => ({ '@id': id, name });

const getBreadcrumbItem = (item, index) => {
  const { '@id': id, name } = item;
  const position = index + 1;

  return {
    '@type': TYPE_LIST_ITEM,
    position,
    item: generateItem(`${BaseUrl}${id}`, name),
  };
};

const createWebsiteSchema = () => {
  const {
    meta_ogSiteName: name = 'Fox Nation',
    meta_ogUrl: url = 'https://nation.foxnews.com/',
  } = window.Language;
  const target = `${url}search?q={search_term_string}`;
  const potentialAction = {
    '@type': SEARCH_ACTION,
    target,
    'query-input': 'required name=search_term_string',
  };
  return {
    '@context': CONTEXT,
    '@type': TYPE_WEBSITE,
    name,
    url,
    potentialAction,
  };
};

const createOrganizationSchema = () => {
  const {
    meta_ogSiteName: name = 'Fox Nation',
    meta_ogUrl: url = 'https://nation.foxnews.com/',
  } = window.Language;
  const sameAs = [
    'https://twitter.com/foxnation',
    'https://www.instagram.com/foxnation',
    'https://www.facebook.com/FoxNation',
    'https://www.youtube.com/@foxnation',
    'https://www.tiktok.com/@foxnation/',
  ];
  const logos = window?.ExternalConfig?.appLogo;
  const desktopSrc = logos?.desktop;
  return {
    '@context': CONTEXT,
    '@type': TYPE_ORGANIZATION,
    name,
    url,
    logo: desktopSrc,
    contactPoint: {
      '@type': CONTACT_POINT,
      telephone: '+1-888-596-3938',
      contactType: 'customer service',
      areaServed: DEFAULT_COUNTRY,
    },
    sameAs,
  };
};

const schemaGenerator = {
  [SCHEMA_TYPES.BREADCRUMBS]: createBreadcrumbSchema,
  [SCHEMA_TYPES.FAQ]: createFAQSchema,
  [SCHEMA_TYPES.MOVIES]: createMoviesSchema,
  [SCHEMA_TYPES.ORGANIZATION]: createOrganizationSchema,
  [SCHEMA_TYPES.PERSON]: createPersonSchema,
  [SCHEMA_TYPES.SERIES]: createSeriesDetailSchema,
  [SCHEMA_TYPES.VIDEO_OBJECT]: createVideoObjectSchema,
  [SCHEMA_TYPES.WATCH_CLIP]: createWatchClipSchema,
  [SCHEMA_TYPES.WATCH_EPISODE]: createWatchEpisodeSchema,
  [SCHEMA_TYPES.WEBSITE]: createWebsiteSchema,
};

export const getTags = schemas =>
  schemas?.map(schema => {
    const type = schema?.type;
    const item = schema?.item;
    return createSchemaTag({ item, type });
  });

const createSchemaTag = ({ item, type }) => {
  if (!item || !type) return null;
  const data = schemaGenerator?.[type]?.({ item });
  const dataJSON = JSON.stringify(data);
  return (
    <script key={`${type}-${item.id}`} type="application/ld+json">
      {dataJSON}
    </script>
  );
};

createSchemaTag.propTypes = {
  type: PropTypes.string.isRequired,
  item: PropTypes.shape({}).isRequired,
};
